import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(n));

// Interactive "loyer → commission → net" calculator (owner conversion tool).
export const CommissionCalculator = ({ rate = 20 }) => {
  const [rent, setRent] = useState(1500);
  const commission = (rent * rate) / 100;
  const net = rent - commission;
  const clamp = (v) => Math.min(10000, Math.max(0, Number.isFinite(v) ? v : 0));

  return (
    <section className="bg-nj-olive/40" data-testid="commission-calculator">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-8">
          <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Calculez en direct</span>
          <h2 className="font-serif font-light text-3xl text-nj-text mt-3">Combien percevrez-vous, commission déduite ?</h2>
          <p className="text-nj-text2 mt-2 text-base">Déplacez le curseur ou saisissez vos loyers mensuels : nous appliquons {rate} % de commission, tout compris.</p>
        </div>

        <div className="bg-white border border-nj-olive rounded-lg p-6 sm:p-9 shadow-[0_1px_0_rgba(28,63,58,0.04)]">
          <label htmlFor="calc-rent" className="block text-sm font-medium text-nj-text mb-2">Vos loyers mensuels encaissés</label>
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <input
                id="calc-rent"
                data-testid="calc-rent-input"
                type="number"
                min="0"
                max="10000"
                step="50"
                value={rent}
                onChange={(e) => setRent(clamp(parseInt(e.target.value, 10)))}
                className="w-full h-12 rounded-md border border-nj-olive bg-white pl-4 pr-9 text-lg font-medium text-nj-text focus:outline-none focus:border-nj-primary"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-nj-muted">€</span>
            </div>
          </div>
          <input
            data-testid="calc-rent-range"
            type="range"
            min="200"
            max="6000"
            step="50"
            value={Math.min(6000, rent)}
            onChange={(e) => setRent(clamp(parseInt(e.target.value, 10)))}
            className="w-full accent-nj-primary cursor-pointer"
            aria-label="Loyers mensuels"
          />

          <div className="grid sm:grid-cols-3 gap-3 mt-8">
            <div className="rounded-md bg-nj-bg border border-nj-olive px-4 py-5 text-center">
              <div className="text-xs uppercase tracking-wide text-nj-muted">Loyers encaissés</div>
              <div className="font-serif text-2xl text-nj-text mt-1" data-testid="calc-rent-value">{eur(rent)}</div>
            </div>
            <div className="rounded-md bg-nj-bg border border-nj-olive px-4 py-5 text-center">
              <div className="text-xs uppercase tracking-wide text-nj-muted">Commission NJ ({rate} %)</div>
              <div className="font-serif text-2xl text-nj-muted mt-1" data-testid="calc-commission">− {eur(commission)}</div>
            </div>
            <div className="rounded-md bg-nj-primary px-4 py-5 text-center">
              <div className="text-xs uppercase tracking-wide text-white/70">Vous percevez</div>
              <div className="font-serif text-3xl text-nj-gold mt-1" data-testid="calc-net">{eur(net)}</div>
            </div>
          </div>

          <p className="text-xs text-nj-muted mt-5 italic text-center">
            Le ménage (payé par le voyageur) et la taxe de séjour (collectée auprès du voyageur) n'entrent pas dans ce calcul. Estimation indicative, hors saisonnalité et taux d'occupation réel.
          </p>

          <div className="flex justify-center mt-6">
            <Link to="/estimation-revenus-airbnb-rouen" data-testid="calc-cta" className="bg-nj-gold text-nj-dark font-semibold px-7 py-3.5 rounded-sm hover:brightness-105 transition-all inline-flex items-center gap-2">
              Estimer les revenus de mon logement <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommissionCalculator;
